import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Button,
    IconButton,
    Chip,
    Stack,
    Grid,
    Card,
    CardContent,
    TablePagination,
    Collapse,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Badge,
    Avatar,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Tabs,
    Tab,
    LinearProgress,
} from '@mui/material';
import {
    FilterList,
    Download,
    Refresh,
    ExpandMore,
    ExpandLess,
    Search,
    DateRange,
    Timeline,
    Security,
    Warning,
    CheckCircle,
    Error as ErrorIcon,
    Person,
    Computer,
    Phone,
    Tablet,
    Api,
    TouchApp,
    Assignment,
    ViewList,
    BarChart as BarChartIcon,
    Update,
    Schedule
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Bar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { auditAPI } from '../../services/auditService';

const USAGE_PRESETS = [
    { id: '7', label: '7 days', days: 6 },
    { id: '14', label: '14 days', days: 13 },
    { id: '30', label: '30 days', days: 29 },
];

function chicagoToday() {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date());
}

function usageFromDate(daysBack) {
    const d = new Date(`${chicagoToday()}T12:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - daysBack);
    return d.toISOString().slice(0, 10);
}

function formatUsageDuration(ms) {
    const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${total}s`;
}

function formatUsageWhen(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}

function usageView(report, userId) {
    if (!report) return null;
    if (!userId) return report;
    const events = (report.events || []).filter((event) => event.userId === userId);
    const time = (report.time || []).filter((row) => row.userId === userId);
    const byAreaMap = new Map();
    (report.areasByUser || []).filter((row) => row.userId === userId).forEach((row) => {
        byAreaMap.set(row.area, (byAreaMap.get(row.area) || 0) + row.count);
    });
    const byDayMap = new Map((report.byDay || []).map((row) => [row.date, { date: row.date, events: 0, durationMs: 0 }]));
    time.forEach((row) => {
        const cur = byDayMap.get(row.date) || { date: row.date, events: 0, durationMs: 0 };
        cur.durationMs += row.durationMs;
        cur.events += row.events || 0;
        byDayMap.set(row.date, cur);
    });
    const known = (report.timeByUser || []).find((row) => row.userId === userId);
    const durationMs = time.reduce((sum, row) => sum + row.durationMs, 0);
    const totalEvents = time.reduce((sum, row) => sum + (row.events || 0), 0);
    return {
        ...report,
        events,
        time,
        totalEvents,
        timeByUser: known ? [{ ...known, durationMs: durationMs || known.durationMs, events: totalEvents || known.events }] : [],
        byArea: [...byAreaMap.entries()].map(([area, count]) => ({ area, count })).sort((a, b) => b.count - a.count),
        byDay: [...byDayMap.values()].sort((a, b) => a.date.localeCompare(b.date)),
        timeByUserScreen: (report.timeByUserScreen || []).filter((row) => row.userId === userId),
        timeByScreen: (report.timeByUserScreen || [])
            .filter((row) => row.userId === userId)
            .map((row) => ({
                screen: row.screen,
                area: row.area,
                durationMs: row.durationMs,
                people: 1,
                phone: row.phone || 0,
                tablet: row.tablet || 0,
                desktop: row.desktop || 0,
            }))
            .sort((a, b) => b.durationMs - a.durationMs || a.screen.localeCompare(b.screen)),
        flows: [...(report.flowsByUser || [])
            .filter((row) => row.userId === userId)
            .reduce((map, row) => {
                const key = `${row.from}|${row.to}`;
                const cur = map.get(key) || { from: row.from, to: row.to, count: 0 };
                cur.count += row.count || 0;
                map.set(key, cur);
                return map;
            }, new Map()).values()]
            .sort((a, b) => b.count - a.count || a.from.localeCompare(b.from))
            .slice(0, 25),
    };
}

const SCREEN_LABELS = {
    dashboard: 'Dashboard',
    'trucks.list': 'Truck list',
    'trucks.detail': 'Truck detail',
    'safety.overview': 'Safety overview',
    'safety.videos': 'Training videos',
    'safety.load-securing': 'Load securing',
    'safety.equipment': 'Equipment',
    'safety.apu': 'APU troubleshooting',
    'safety.apu-guide': 'APU guide',
    'safety.training': 'Required training',
    'safety.watch-tracking': 'Watch tracking',
    'safety.analytics': 'Safety analytics',
    'safety.driver-scores': 'Driver scores',
    'safety.fleet-dashboard': 'Fleet safety',
    'safety.compliance': 'Compliance',
    'forms.overview': 'Forms overview',
    'forms.time-off': 'Time off',
    'forms.apparel': 'Apparel',
    'forms.weekly-checklist': 'Weekly checklist',
    'forms.management': 'Form admin',
    'forms.management.time-off': 'Form admin · Time off',
    'forms.management.apparel': 'Form admin · Apparel',
    'forms.management.checklists': 'Form admin · Checklists',
    'feedback.browse': 'Feedback',
    'feedback.submit': 'Submit feedback',
    'feedback.roadmap': 'Feedback roadmap',
    'feedback.detail': 'Feedback detail',
    recruitment: 'Recruitment',
    'recruitment.application': 'Application',
    'recruitment.map': 'Applicant map',
    quotes: 'Quotes',
    'quotes.detail': 'Quote detail',
    customers: 'Customers',
    'customers.table': 'Customer list',
    'customers.cards': 'Customer cards',
    'customers.map': 'Customer map',
    'customers.detail': 'Customer detail',
    'customers.edit': 'Customer edit',
    admin: 'Admin',
    'admin.companies': 'Admin · Companies',
    'admin.settings': 'Admin · Company settings',
    'admin.users': 'Admin · Users',
    'admin.access': 'Admin · Customer access',
    'admin.spotlight': 'Admin · Driver spotlight',
    'admin.audit': 'Admin · Audit',
    'admin.integrations': 'Admin · Integrations',
    'admin.api-keys': 'Admin · API keys',
    'admin.data': 'Admin · Data',
    'auth.login': 'Sign in',
    'auth.logout': 'Sign out',
};

function screenLabel(screen) {
    if (SCREEN_LABELS[screen]) return SCREEN_LABELS[screen];
    return String(screen || 'Screen').replace(/[.-]/g, ' ');
}

const AUDIENCE_LABELS = { driver: 'Drivers', office: 'Office', admin: 'Admins' };
const VIEWPORT_LABELS = { phone: 'Phone', tablet: 'Tablet', desktop: 'Desktop' };

// Action icon mapping
const ACTION_ICONS = {
    page_visit: ViewList,
    button_click: TouchApp,
    form_submit: Assignment,
    api_call: Api,
    login: CheckCircle,
    logout: Security,
    create: CheckCircle,
    update: CheckCircle,
    delete: Warning,
    view: ViewList,
    download: Download,
    upload: CheckCircle,
    search: Search,
    filter: FilterList,
    export: Download,
    navigation: Timeline
};

// Risk level colors
const RISK_COLORS = {
    low: 'success',
    medium: 'warning',
    high: 'error'
};

// Device type icons
const DEVICE_ICONS = {
    desktop: Computer,
    mobile: Phone,
    tablet: Tablet
};

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`audit-tabpanel-${index}`}
            aria-labelledby={`audit-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function AuditManagement() {
    // Main state
    const [activeTab, setActiveTab] = useState(0);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageRange, setUsageRange] = useState('14');
    const [usagePerson, setUsagePerson] = useState('');
    const [realTimeActivity, setRealTimeActivity] = useState([]);
    const [error, setError] = useState(null);
    
    // Filters and pagination
    const [filters, setFilters] = useState({
        search: '',
        userId: '',
        action: '',
        resource: '',
        riskLevel: '',
        isSuccess: '',
        startDate: null,
        endDate: null
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc');
    const [totalCount, setTotalCount] = useState(0);
    
    // UI state
    const [expandedRow, setExpandedRow] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [userDetailDialog, setUserDetailDialog] = useState({ open: false, userId: null });
    const [autoRefresh, setAutoRefresh] = useState(true);
    
    // Available filter options (will be populated from data)
    const [filterOptions, setFilterOptions] = useState({
        users: [],
        actions: [],
        resources: []
    });

    // Fetch audit logs
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy,
                sortOrder,
                ...filters,
                startDate: filters.startDate ? filters.startDate.toISOString() : null,
                endDate: filters.endDate ? filters.endDate.toISOString() : null
            };
            
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });
            
            const response = await auditAPI.getLogs(params);
            setLogs(response.data.logs);
            setTotalCount(response.data.pagination.total);
            
            // Extract filter options from data
            const users = [...new Set(response.data.logs.map(log => log.userId).filter(Boolean))];
            const actions = [...new Set(response.data.logs.map(log => log.action))];
            const resources = [...new Set(response.data.logs.map(log => log.resource))];
            
            setFilterOptions({ users, actions, resources });
            
        } catch (err) {
            setError('Failed to fetch audit logs');
            console.error('Error fetching logs:', err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, sortBy, sortOrder, filters]);

    const fetchUsage = useCallback(async () => {
        setUsageLoading(true);
        try {
            const preset = USAGE_PRESETS.find((item) => item.id === usageRange) || USAGE_PRESETS[1];
            const body = await auditAPI.getUsage({
                from: usageFromDate(preset.days),
                to: chicagoToday(),
            });
            setUsage(body.data);
        } catch (err) {
            setError('Failed to load usage');
            console.error('Error fetching usage:', err);
        } finally {
            setUsageLoading(false);
        }
    }, [usageRange]);

    // Fetch real-time activity
    const fetchRealTimeActivity = useCallback(async () => {
        try {
            const response = await auditAPI.getRealTimeActivity();
            setRealTimeActivity(response.data.activities);
        } catch (err) {
            console.error('Error fetching real-time activity:', err);
        }
    }, []);

    // Effects
    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        if (activeTab === 1) {
            fetchUsage();
        } else if (activeTab === 2) {
            fetchRealTimeActivity();
        }
    }, [activeTab, fetchUsage, fetchRealTimeActivity]);

    // Auto-refresh real-time data
    useEffect(() => {
        if (autoRefresh && activeTab === 2) {
            const interval = setInterval(fetchRealTimeActivity, 10000); // Every 10 seconds
            return () => clearInterval(interval);
        }
    }, [autoRefresh, activeTab, fetchRealTimeActivity]);

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0); // Reset to first page when filtering
    };

    // Handle export
    const handleExport = async (format = 'csv') => {
        try {
            const params = {
                format,
                ...filters,
                startDate: filters.startDate ? filters.startDate.toISOString() : null,
                endDate: filters.endDate ? filters.endDate.toISOString() : null
            };
            
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });
            
            const response = await auditAPI.exportLogs(params);
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit-logs-${Date.now()}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
        } catch (err) {
            setError('Failed to export audit logs');
            console.error('Export error:', err);
        }
    };

    // Handle user detail view
    const handleUserDetail = async (userId) => {
        setUserDetailDialog({ open: true, userId, loading: true, data: null });
        
        try {
            const response = await auditAPI.getUserActivity(userId, { limit: 50 });
            setUserDetailDialog(prev => ({ ...prev, loading: false, data: response.data }));
        } catch (err) {
            setUserDetailDialog(prev => ({ ...prev, loading: false, error: 'Failed to load user activity' }));
        }
    };

    // Format helpers
    const formatTimestamp = (timestamp) => {
        const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
        return format(date, 'MMM dd, yyyy HH:mm:ss');
    };

    const formatDuration = (duration) => {
        if (!duration) return 'N/A';
        if (duration < 1000) return `${duration}ms`;
        return `${(duration / 1000).toFixed(1)}s`;
    };

    const getActionIcon = (action) => {
        const IconComponent = ACTION_ICONS[action] || Api;
        return <IconComponent fontSize="small" />;
    };

    const getDeviceIcon = (device) => {
        const IconComponent = DEVICE_ICONS[device] || Computer;
        return <IconComponent fontSize="small" />;
    };

    // Render filter controls
    const renderFilters = () => (
        <Collapse in={showFilters}>
            <Paper sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Search"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                            }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={filters.action}
                                label="Action"
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                            >
                                <MenuItem value="">All Actions</MenuItem>
                                {filterOptions.actions.map(action => (
                                    <MenuItem key={action} value={action}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {getActionIcon(action)}
                                            <span>{action.replace(/_/g, ' ').toUpperCase()}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Resource</InputLabel>
                            <Select
                                value={filters.resource}
                                label="Resource"
                                onChange={(e) => handleFilterChange('resource', e.target.value)}
                            >
                                <MenuItem value="">All Resources</MenuItem>
                                {filterOptions.resources.map(resource => (
                                    <MenuItem key={resource} value={resource}>
                                        {resource.replace(/_/g, ' ').toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Risk Level</InputLabel>
                            <Select
                                value={filters.riskLevel}
                                label="Risk Level"
                                onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
                            >
                                <MenuItem value="">All Levels</MenuItem>
                                <MenuItem value="low">
                                    <Chip label="Low" color="success" size="small" />
                                </MenuItem>
                                <MenuItem value="medium">
                                    <Chip label="Medium" color="warning" size="small" />
                                </MenuItem>
                                <MenuItem value="high">
                                    <Chip label="High" color="error" size="small" />
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="Start Date"
                                value={filters.startDate}
                                onChange={(value) => handleFilterChange('startDate', value)}
                                renderInput={(props) => <TextField {...props} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="End Date"
                                value={filters.endDate}
                                onChange={(value) => handleFilterChange('endDate', value)}
                                renderInput={(props) => <TextField {...props} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.isSuccess}
                                label="Status"
                                onChange={(e) => handleFilterChange('isSuccess', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="true">Success</MenuItem>
                                <MenuItem value="false">Failed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                onClick={() => setFilters({
                                    search: '',
                                    userId: '',
                                    action: '',
                                    resource: '',
                                    riskLevel: '',
                                    isSuccess: '',
                                    startDate: null,
                                    endDate: null
                                })}
                                fullWidth
                            >
                                Clear Filters
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>
        </Collapse>
    );

    // Render activity logs table
    const renderLogsTable = () => (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 800 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Resource</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Risk</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((log) => (
                            <React.Fragment key={log._id}>
                                <TableRow 
                                    hover 
                                    sx={{ '& > *': { borderBottom: 'unset' } }}
                                    onClick={() => setExpandedRow(expandedRow === log._id ? null : log._id)}
                                >
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatTimestamp(log.timestamp)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDistanceToNow(parseISO(log.timestamp), { addSuffix: true })}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 24, height: 24 }}>
                                                <Person fontSize="small" />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">
                                                    {log.userId?.name || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {log.userId?.role || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {getActionIcon(log.action)}
                                            <Typography variant="body2">
                                                {log.action.replace(/_/g, ' ').toUpperCase()}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Chip 
                                            label={log.resource.replace(/_/g, ' ').toUpperCase()} 
                                            size="small" 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {log.endpoint}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {log.isSuccess ? (
                                                <CheckCircle color="success" fontSize="small" />
                                            ) : (
                                                <ErrorIcon color="error" fontSize="small" />
                                            )}
                                            <Typography variant="body2">
                                                {log.statusCode || 'N/A'}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Chip 
                                            label={log.riskLevel.toUpperCase()} 
                                            color={RISK_COLORS[log.riskLevel]}
                                            size="small"
                                        />
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDuration(log.details?.duration || log.responseTime)}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <IconButton size="small">
                                            {expandedRow === log._id ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                
                                <TableRow>
                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                        <Collapse in={expandedRow === log._id} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 1 }}>
                                                <Typography variant="h6" gutterBottom component="div">
                                                    Activity Details
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <Typography variant="subtitle2">Session Information</Typography>
                                                        <Typography variant="body2">Session ID: {log.sessionId}</Typography>
                                                        <Typography variant="body2">IP Address: {log.ipAddress}</Typography>
                                                        <Typography variant="body2">
                                                            Device: {getDeviceIcon(log.browserInfo?.device)} {log.browserInfo?.device || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Browser: {log.browserInfo?.name} {log.browserInfo?.version}
                                                        </Typography>
                                                        <Typography variant="body2">OS: {log.browserInfo?.os}</Typography>
                                                    </Grid>
                                                    
                                                    {log.details && Object.keys(log.details).length > 0 && (
                                                        <Grid item xs={12} md={6}>
                                                            <Typography variant="subtitle2">Activity Details</Typography>
                                                            <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                                                                {JSON.stringify(log.details, null, 2)}
                                                            </pre>
                                                        </Grid>
                                                    )}
                                                    
                                                    {log.performanceMetrics && Object.keys(log.performanceMetrics).length > 0 && (
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2">Performance Metrics</Typography>
                                                            <pre style={{ fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                                                                {JSON.stringify(log.performanceMetrics, null, 2)}
                                                            </pre>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
            />
        </Paper>
    );

    const renderAnalytics = () => {
        const view = usageView(usage, usagePerson);
        const peopleMap = new Map();
        (usage?.events || []).forEach((event) => {
            if (event.userId) peopleMap.set(event.userId, event.displayName || event.userId);
        });
        (usage?.timeByUser || []).forEach((row) => {
            if (row.userId) peopleMap.set(row.userId, row.displayName || peopleMap.get(row.userId) || row.userId);
        });
        const people = [...peopleMap.entries()]
            .map(([userId, displayName]) => ({ userId, displayName }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        const listedPeople = (usage?.timeByUser || []).map((row) => ({
            userId: row.userId,
            displayName: row.displayName || peopleMap.get(row.userId) || 'Unknown',
        }));
        const personOptions = listedPeople.length ? listedPeople : people;
        const totalMs = (view?.timeByUser || []).reduce((sum, row) => sum + row.durationMs, 0);
        const peopleCount = usagePerson ? (view?.timeByUser || []).length : (usage?.timeByUser || []).length;
        const maxPersonMs = Math.max(1, ...(view?.timeByUser || []).map((row) => row.durationMs || 0));
        const personData = (view?.timeByUser || []).map((row) => ({
            userId: row.userId,
            name: row.displayName || 'Unknown',
            durationMs: row.durationMs || 0,
        }));
        const dayData = (view?.byDay || []).map((row) => ({
            date: String(row.date || '').slice(5),
            events: row.events,
            minutes: Math.round(row.durationMs / 60000),
        }));
        const areaData = (view?.byArea || []).map((row) => ({
            area: row.area,
            count: row.count,
        }));
        const recent = (view?.events || []).slice(0, 20);
        const hasUsage = (view?.totalEvents || 0) > 0 || (view?.timeByUser || []).length > 0;
        const screenRows = view?.timeByScreen || [];
        const screenTimeMs = screenRows.reduce((sum, row) => sum + (row.durationMs || 0), 0);
        const maxScreenMs = Math.max(1, ...screenRows.map((row) => row.durationMs || 0));
        const viewportRows = ['phone', 'tablet', 'desktop']
            .map((viewport) => ({
                viewport,
                durationMs: screenRows.reduce((sum, row) => sum + (row[viewport] || 0), 0),
            }))
            .filter((row) => row.durationMs > 0);
        const audienceRows = usagePerson
            ? [...(view?.timeByUserScreen || []).reduce((map, row) => {
                if (!row.audience) return map;
                const cur = map.get(row.audience) || { audience: row.audience, durationMs: 0, people: 1 };
                cur.durationMs += row.durationMs || 0;
                map.set(row.audience, cur);
                return map;
            }, new Map()).values()]
            : (view?.byAudience || []);

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                            <Typography variant="h6">Usage</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel>Person</InputLabel>
                                    <Select
                                        label="Person"
                                        value={usagePerson}
                                        onChange={(e) => setUsagePerson(e.target.value)}
                                    >
                                        <MenuItem value="">Everyone</MenuItem>
                                        {personOptions.map((person) => (
                                            <MenuItem key={person.userId} value={person.userId}>
                                                {person.displayName || person.userId}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <ToggleButtonGroup
                                    value={usageRange}
                                    exclusive
                                    onChange={(_, value) => value && setUsageRange(value)}
                                    size="small"
                                >
                                    {USAGE_PRESETS.map((preset) => (
                                        <ToggleButton key={preset.id} value={preset.id}>{preset.label}</ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {usageLoading && !view && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    </Grid>
                )}

                {view && (
                    <>
                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4">{(view.totalEvents ?? view.events.length).toLocaleString()}</Typography>
                                    <Typography color="text.secondary">Events</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4">{peopleCount}</Typography>
                                    <Typography color="text.secondary">
                                        {view.accountCount && !usagePerson ? `Active of ${view.accountCount} users` : 'People'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4">{formatUsageDuration(totalMs)}</Typography>
                                    <Typography color="text.secondary">Time in app</Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {!hasUsage && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    No usage recorded in this range yet. Page views and time show up after people move through the app.
                                </Alert>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6">Where time is spent</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Measured time on each screen, split by phone, tablet, and desktop, and by drivers, office staff, and admins. This is the record a future layout can use. It starts with this release, separate from the activity history above.
                                </Typography>
                                {screenTimeMs <= 0 ? (
                                    <Typography color="text.secondary">No measured screen time in this range yet.</Typography>
                                ) : (
                                    <>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                                            <Chip size="small" label={`${formatUsageDuration(screenTimeMs)} on screens`} />
                                            {viewportRows.map((row) => (
                                                <Chip key={row.viewport} size="small" variant="outlined" label={`${VIEWPORT_LABELS[row.viewport] || row.viewport} ${formatUsageDuration(row.durationMs)}`} />
                                            ))}
                                            {audienceRows.map((row) => (
                                                <Chip key={row.audience} size="small" variant="outlined" label={`${AUDIENCE_LABELS[row.audience] || row.audience} ${formatUsageDuration(row.durationMs)}${row.people ? ` · ${row.people}` : ''}`} />
                                            ))}
                                        </Stack>
                                        <Box sx={{ maxHeight: 420, overflow: 'auto', pr: 1 }}>
                                            {screenRows.map((row) => (
                                                <Box key={row.screen} sx={{ mb: 1.5 }}>
                                                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                        <Typography noWrap sx={{ maxWidth: '70%' }}>{screenLabel(row.screen)}</Typography>
                                                        <Typography variant="body2">
                                                            {formatUsageDuration(row.durationMs)}
                                                            {row.people > 1 ? ` · ${row.people} people` : ''}
                                                        </Typography>
                                                    </Stack>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(row.durationMs / maxScreenMs) * 100}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                        {(view?.flows || []).length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>Common moves</Typography>
                                                <Stack spacing={0.5}>
                                                    {(view.flows || []).slice(0, 8).map((row) => (
                                                        <Typography key={`${row.from}-${row.to}`} variant="body2">
                                                            {screenLabel(row.from)} → {screenLabel(row.to)} · {row.count}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Time by person</Typography>
                                {personData.length === 0 ? (
                                    <Typography color="text.secondary">No activity in this range.</Typography>
                                ) : (
                                    <Box sx={{ maxHeight: 480, overflow: 'auto', pr: 1 }}>
                                        {personData.map((row) => (
                                            <Box key={row.userId} sx={{ mb: 1.5 }}>
                                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                    <Typography noWrap sx={{ maxWidth: '70%' }}>{row.name}</Typography>
                                                    <Typography variant="body2">{formatUsageDuration(row.durationMs)}</Typography>
                                                </Stack>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={(row.durationMs / maxPersonMs) * 100}
                                                    sx={{ height: 8, borderRadius: 4 }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Daily usage</Typography>
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart data={dayData} margin={{ left: 0, right: 8, top: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis yAxisId="events" allowDecimals={false} />
                                        <YAxis yAxisId="minutes" orientation="right" />
                                        <ChartTooltip />
                                        <Bar yAxisId="events" dataKey="events" fill="#90caf9" name="Events" />
                                        <Line yAxisId="minutes" type="monotone" dataKey="minutes" stroke="#1565c0" name="Minutes" dot={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Areas</Typography>
                                {areaData.length === 0 ? (
                                    <Typography color="text.secondary">No page views yet.</Typography>
                                ) : (
                                    <ResponsiveContainer width="100%" height={Math.max(220, areaData.length * 36)}>
                                        <BarChart data={areaData} layout="vertical" margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" allowDecimals={false} />
                                            <YAxis type="category" dataKey="area" width={110} />
                                            <ChartTooltip />
                                            <Bar dataKey="count" fill="#42a5f5" name="Views" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Recent activity</Typography>
                                {view.truncated && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Showing the newest 3,000 events in this range.
                                    </Alert>
                                )}
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Person</TableCell>
                                                <TableCell>Area</TableCell>
                                                <TableCell>Screen</TableCell>
                                                <TableCell>When</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recent.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4}>
                                                        <Typography color="text.secondary">No page views or sign-ins in this range.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {recent.map((event) => (
                                                <TableRow key={event.id}>
                                                    <TableCell>{event.displayName}</TableCell>
                                                    <TableCell>{event.area}</TableCell>
                                                    <TableCell>{event.screen ? screenLabel(event.screen) : (event.path || event.action)}</TableCell>
                                                    <TableCell>{formatUsageWhen(event.at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </>
                )}
            </Grid>
        );
    };

    // Render real-time activity
    const renderRealTime = () => (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">Real-Time Activity Feed</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Badge color="success" variant="dot" invisible={!autoRefresh}>
                        <Typography variant="body2">Auto-refresh</Typography>
                    </Badge>
                    <Button
                        startIcon={<Update />}
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        variant={autoRefresh ? "contained" : "outlined"}
                        size="small"
                    >
                        {autoRefresh ? 'On' : 'Off'}
                    </Button>
                    <Button
                        startIcon={<Refresh />}
                        onClick={fetchRealTimeActivity}
                        size="small"
                    >
                        Refresh
                    </Button>
                </Stack>
            </Stack>
            
            <Paper sx={{ maxHeight: 600, overflow: 'auto' }}>
                {realTimeActivity.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography color="text.secondary">
                            No recent activity in the last 5 minutes
                        </Typography>
                    </Box>
                ) : (
                    realTimeActivity.map((activity) => (
                        <Box key={activity._id} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ width: 32, height: 32 }}>
                                    {getActionIcon(activity.action)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" component="div">
                                        <strong>{activity.userId?.name}</strong> performed{' '}
                                        <Chip label={activity.action.replace(/_/g, ' ')} size="small" sx={{ mx: 0.5 }} />
                                        on <strong>{activity.resource}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatTimestamp(activity.timestamp)} • {activity.endpoint}
                                    </Typography>
                                </Box>
                                <Chip 
                                    label={activity.riskLevel} 
                                    color={RISK_COLORS[activity.riskLevel]}
                                    size="small"
                                />
                            </Stack>
                        </Box>
                    ))
                )}
            </Paper>
        </Box>
    );

    return (
        <Box>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            System Audit & Activity Tracking
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Monitor user activities, system performance, and security events
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Show/Hide Filters">
                            <IconButton 
                                color="inherit" 
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                            >
                                <FilterList />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export CSV">
                            <IconButton 
                                color="inherit" 
                                onClick={() => handleExport('csv')}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                            >
                                <Download />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh Data">
                            <IconButton 
                                color="inherit" 
                                onClick={fetchLogs}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    variant="fullWidth"
                >
                    <Tab icon={<ViewList />} label="Activity Logs" />
                                    <Tab icon={<BarChartIcon />} label="Analytics" />
                <Tab icon={<Update />} label="Real-Time" />
                </Tabs>
            </Paper>

            {/* Tab Panels */}
            <TabPanel value={activeTab} index={0}>
                {renderFilters()}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    renderLogsTable()
                )}
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                {renderAnalytics()}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                {renderRealTime()}
            </TabPanel>

            {/* User Detail Dialog */}
            <Dialog 
                open={userDetailDialog.open} 
                onClose={() => setUserDetailDialog({ open: false, userId: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>User Activity Details</DialogTitle>
                <DialogContent>
                    {userDetailDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : userDetailDialog.error ? (
                        <Alert severity="error">{userDetailDialog.error}</Alert>
                    ) : (
                        userDetailDialog.data && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Activity Summary
                                </Typography>
                                <Typography>
                                    Total Activities: {userDetailDialog.data.summary.totalActivities}
                                </Typography>
                                <Typography>
                                    Sessions: {userDetailDialog.data.summary.uniqueSessions}
                                </Typography>
                                {/* Add more user activity details here */}
                            </Box>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserDetailDialog({ open: false, userId: null })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AuditManagement; 